/**
 * DriverCoverageZones — driver picks which zones inside their operating city
 * they want to receive orders from. Renders a colored chip grid backed by
 * `GET /api/driver/me/zones` + `PATCH /api/driver/me/zones`.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Save, MapPin, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { toast } from 'sonner';

const DriverCoverageZones = () => {
  const [state, setState] = useState({ loading: true, city: null, zones: [], picked: new Set() });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const d = await api.get('/driver/me/zones').then((r) => r.data);
      setState({
        loading: false,
        city: d.city,
        zones: d.zones || [],
        picked: new Set(d.picked_zone_ids || []),
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      toast.error(e?.response?.data?.detail || 'Could not load zones');
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setState((s) => {
      const p = new Set(s.picked);
      if (p.has(id)) p.delete(id); else p.add(id);
      return { ...s, picked: p };
    });
    setDirty(true);
  };
  const selectAll = () => {
    setState((s) => ({ ...s, picked: new Set(s.zones.map((z) => z.id)) }));
    setDirty(true);
  };
  const clearAll = () => {
    setState((s) => ({ ...s, picked: new Set() }));
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/driver/me/zones', { zone_ids: Array.from(state.picked) });
      setDirty(false);
      toast.success(`Coverage saved — you will receive orders from ${state.picked.size} of ${state.zones.length} ${state.city} zones.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (state.loading) {
    return <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading coverage zones…</section>;
  }
  if (!state.city) {
    return (
      <section className="mt-6 bg-white border border-amber-200 rounded-2xl p-5 text-sm text-amber-800" data-testid="coverage-zones-blocked">
        Set a home address in Ottawa or Ibadan first to pick your delivery zones.
      </section>
    );
  }

  return (
    <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="coverage-zones-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Coverage zones · {state.city}</h2>
          </div>
          <p className="text-sm text-slate-500">
            Pick the neighborhoods you want to serve. You&apos;ll only see orders from these zones on your Available list.{' '}
            <span className="text-slate-400">({state.picked.size} of {state.zones.length} selected)</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="coverage-select-all">
            <ChevronsUpDown className="h-3.5 w-3.5 mr-1" />Select all
          </Button>
          <Button variant="outline" size="sm" onClick={clearAll} data-testid="coverage-clear">Clear</Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="coverage-zones-grid">
        {state.zones.map((z) => {
          const on = state.picked.has(z.id);
          return (
            <button
              key={z.id}
              type="button"
              onClick={() => toggle(z.id)}
              className={`group relative text-left rounded-xl border p-2.5 pl-8 transition ${on ? 'border-slate-300 bg-white shadow-sm' : 'border-slate-200 bg-slate-50 hover:bg-white'}`}
              style={{ borderLeftColor: z.color, borderLeftWidth: 5 }}
              data-testid={`coverage-zone-${z.id}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`h-4 w-4 rounded-md border ${on ? '' : 'border-slate-300'}`}
                  style={{ backgroundColor: on ? z.color : 'transparent' }}
                >
                  {on && <Check className="h-3 w-3 text-white -mt-px ml-px" />}
                </span>
                <span className="text-sm font-medium text-slate-900 truncate flex-1">{z.name}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={!dirty || saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="coverage-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save coverage
        </Button>
      </div>
    </section>
  );
};

export default DriverCoverageZones;
